'use client';

import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { appendSearchParamsToUrl, sanitizeUrl } from '@bridge/utils';

import type { SortingState, Updater } from '@tanstack/react-table';

export default function useTableSorting({
  initialSortState = [],
  sortKeyMap = {},
}: {
  initialSortState?: SortingState;
  sortKeyMap?: Record<string, string>;
} = {}) {
  const initialSortStateRef = useRef<SortingState>(initialSortState);
  const searchParams = useSearchParams();

  // Memoize the inverse mapping to avoid recreating on every render
  const sortKeyMapInverse = useMemo(
    () => Object.fromEntries(Object.entries(sortKeyMap).map(([key, value]) => [value, key])),
    [sortKeyMap]
  );

  // Derive initial sorting state from URL parameters
  const sortingFromUrl = useMemo(() => {
    const sortBy = searchParams?.get('sortBy');
    const sortDirection = searchParams?.get('sortDirection');

    if (!sortBy) return initialSortState;

    const columnId = sortKeyMapInverse[sortBy];
    if (!columnId) return initialSortState;
    return [{ id: columnId, desc: sortDirection === 'desc' }];
  }, [searchParams, sortKeyMapInverse, initialSortState]);

  const setSorting = useCallback(
    (updater: Updater<SortingState>) => {
      // Resolve the updater to get the actual sorting state
      const newSortingState = typeof updater === 'function' ? updater(sortingFromUrl) : updater;

      // Determine if we should clear or set sort parameters
      const isDefaultSorting = JSON.stringify(newSortingState) === JSON.stringify(initialSortStateRef.current);

      let sortBy = '';
      let sortDirection = '';

      if (!isDefaultSorting && newSortingState.length > 0) {
        sortBy = newSortingState.map((sort) => sortKeyMap[sort.id] || sort.id).join(',');
        sortDirection = newSortingState.map((sort) => (sort.desc ? 'desc' : 'asc')).join(',');
      }

      const baseUrl = window.location.origin;
      const newUrl = appendSearchParamsToUrl(
        window.location.href,
        { sortBy, sortDirection, nextCursor: '', prevCursor: '' },
        baseUrl,
      );

      window.history.pushState(null, '', sanitizeUrl(newUrl));
    },
    [sortingFromUrl, sortKeyMap]
  );

  return {
    sorting: sortingFromUrl,
    setSorting,
  };
}
